// Nach dem Senden (E2, Schritt 4; Mockup /doku/gesendet): ein eigener,
// dunkler Bildschirm, der live zeigt, wie es steht – aus dem Speicher am
// Gerät, nicht aus einer Vermutung:
//  - im Büro: Haken, „Ist im Büro!“ – der Server hat bestätigt, dass alles
//    da ist; im Büro steht sie unter „Zu tun“ (seit E3c – vorher „zu prüfen“).
//  - abgeschlossen, mit Netz: „Wird gesendet …“ und was noch wartet.
//  - abgeschlossen, ohne Netz: Uhr, „Wartet auf Empfang“ – geht von selbst.
//  - ein dauerhafter Fehler: rot, mit „Nochmal senden“.
// Den Knopf „Im Büro ansehen“ aus dem Mockup gibt es in der Handy-App nicht –
// das Büro kommt mit E3. Statt dessen: die Besichtigung zum Nachsehen.
//
// Seit E3 (S7, Gernots Entscheidung 4) steht hier der Montagetermin, mit dem
// Kunden vereinbart – Tag und Text, gleich noch beim Kunden (termin.tsx);
// die Partie-Planung bleibt Platzhalter (Kiste Termin).
//
// Seit Schritt 5 derselbe Bildschirm nach „Nachtrag senden“ – „Nachtrag ist
// im Büro!“ –, für den jüngsten Nachtrag der Besichtigung: dieselben Stände
// (es läuft immer nur einer), dieselben Knöpfe, ohne den Platzhalter
// „Termin“ (der gehört zur ersten Sendung). Eine eigene Adresse statt eines
// Merkers: So bleibt „Zurück“ im Browser ehrlich, und die Übersicht kann
// direkt hierher führen.
//
// Adressen: #/aufnahme/<kennung>/gesendet
//           #/aufnahme/<kennung>/nachtrag/gesendet

import type { IchAntwort } from "@happy-energy/kern";
import { Chip, Ikon, Spaeter, TestBand, Tipp, useNetz, type IkonName } from "@happy-energy/oberflaeche";
import { speicher, type Aufnahme, type Nachtrag } from "../speicher.ts";
import { erneutVersuchen } from "../postausgang.ts";
import { useLive, usePostausgang } from "./gemeinsam.tsx";
import { MontageterminKarte } from "./termin.tsx";

interface Anzeige {
  zeichen: IkonName | "kreisel";
  art: "fertig" | "wartet" | "fehler";
  titel: string;
  text: string;
}

/** Wo der jüngste Nachtrag steht – mit denselben Fällen wie die erste Sendung. */
function nachtragAnzeige(aufnahme: Aufnahme, n: Nachtrag | undefined, netz: boolean): Anzeige {
  if (!n) return { zeichen: "achtung", art: "fehler", titel: "Kein Nachtrag", text: "Zu dieser Besichtigung ist am Gerät kein Nachtrag gespeichert." };
  if (aufnahme.fehler) {
    return { zeichen: "achtung", art: "fehler", titel: "Hängt noch fest", text: "Am Gerät ist alles sicher gespeichert – nur beim Büro ist noch nicht alles angekommen." };
  }
  if (n.stand === "im_buero") {
    // Der Name steht für sich (nach Doppelpunkt): „von Herr …“ wäre falsch gebeugt, und Namen sind frei getippt.
    return {
      zeichen: "haken",
      art: "fertig",
      titel: "Nachtrag ist im Büro!",
      text: `Nachgereicht zu: ${aufnahme.kunde.name}, ${aufnahme.objekt.strasse}. Im Büro ist es als neu erkennbar.`,
    };
  }
  if (n.stand === "offen") return { zeichen: "uhr", art: "wartet", titel: "Noch nicht gesendet", text: "Erst „Nachtrag senden“ – dann geht er ans Büro." };
  return netz
    ? { zeichen: "kreisel", art: "wartet", titel: "Nachtrag wird gesendet …", text: "Fotos und Sprachmemos gehen zuerst – bei schwachem Empfang dauert das etwas. Die App bitte offen lassen." }
    : { zeichen: "uhr", art: "wartet", titel: "Wartet auf Empfang", text: "Geht von selbst raus, sobald wieder Netz da ist – die App muss dafür offen sein." };
}

export function GesendetSeite({ ich, aufnahmeId, nachtrag = false }: { ich: IchAntwort; aufnahmeId: string; nachtrag?: boolean }) {
  const daten = useLive(async () => {
    const aufnahme = await speicher.aufnahmen.get(aufnahmeId);
    if (!aufnahme) return null;
    const nachtraege = await speicher.nachtraege.where("aufnahmeId").equals(aufnahmeId).sortBy("begonnenAm");
    return { aufnahme, letzter: nachtraege.at(-1) };
  }, aufnahmeId);
  const post = usePostausgang();
  const netz = useNetz();
  if (daten === undefined) return <div className="gesendet-seite" />;

  const aufnahme = daten?.aufnahme;
  const letzter = daten?.letzter;
  const zurBesichtigung = `#/aufnahme/${aufnahmeId}`;
  const wartet = post.jeAufnahme[aufnahmeId] ?? 0;
  const name = aufnahme?.kunde.name ?? "";

  const anzeige: Anzeige = !aufnahme
    ? { zeichen: "achtung", art: "fehler", titel: "Nicht gefunden", text: "Diese Besichtigung ist auf diesem Gerät nicht gespeichert." }
    : nachtrag
      ? nachtragAnzeige(aufnahme, letzter, netz)
      : aufnahme.fehler
        ? { zeichen: "achtung", art: "fehler", titel: "Hängt noch fest", text: "Am Gerät ist alles sicher gespeichert – nur beim Büro ist noch nicht alles angekommen." }
        : aufnahme.stand === "im_buero"
          ? { zeichen: "haken", art: "fertig", titel: "Ist im Büro!", text: `${name}, ${aufnahme.objekt.strasse} – steht jetzt im Büro unter „Zu tun“.` }
          : aufnahme.stand === "in_aufnahme"
            ? { zeichen: "uhr", art: "wartet", titel: "Noch nicht gesendet", text: "Erst abschließen – dann geht sie ans Büro." }
            : netz
              ? { zeichen: "kreisel", art: "wartet", titel: "Wird gesendet …", text: "Fotos und Sprachmemos gehen zuerst – bei schwachem Empfang dauert das etwas. Die App bitte offen lassen." }
              : { zeichen: "uhr", art: "wartet", titel: "Wartet auf Empfang", text: "Geht von selbst raus, sobald wieder Netz da ist – die App muss dafür offen sein." };
  // Unterwegs: die erste Sendung, die abgeschlossen ist – oder der Nachtrag, der gesendet wird.
  const unterwegs = nachtrag ? letzter?.stand === "abgeschlossen" : aufnahme?.stand === "abgeschlossen";
  // Noch nicht abgeschlossen: dorthin, wo es weitergeht.
  const offen = nachtrag ? letzter?.stand === "offen" : aufnahme?.stand === "in_aufnahme";

  return (
    <div className="gesendet-seite">
      <TestBand umgebung={ich.umgebung} />
      <main className="gesendet-inhalt">
        <span className={`gesendet-zeichen ${anzeige.art}`} aria-hidden="true">
          {anzeige.zeichen === "kreisel" ? <span className="kreisel" /> : <Ikon name={anzeige.zeichen} groesse="g" />}
        </span>
        <h1 aria-live="polite">{anzeige.titel}</h1>
        <p>{anzeige.text}</p>
        {unterwegs && !aufnahme?.fehler && wartet > 0 && (
          <Chip art="dunkel" ikon={netz ? "uhr" : "offline"}>
            {wartet} wartet
          </Chip>
        )}

        {aufnahme?.fehler && (
          <Tipp art="rot" ikon="achtung">
            {aufnahme.fehler}
            <button type="button" className="tipp-knopf" onClick={() => void erneutVersuchen(aufnahme.id)}>
              Nochmal senden
            </button>
          </Tipp>
        )}

        {/* Seit E3 (S7) statt des Platzhalters: den Montagetermin gleich hier eintragen – die Eingabe überlebt den Kalender. */}
        {!nachtrag && aufnahme && aufnahme.stand !== "in_aufnahme" && (
          <>
            <MontageterminKarte aufnahme={aufnahme} dunkel />
            <Spaeter titel="Freie Partie gleich vorschlagen" wann="Kiste Termin">
              Die Montage-Partie mit freiem Platz in rund drei Wochen – direkt aus dem Terminmanagement, noch beim Kunden.
            </Spaeter>
          </>
        )}

        <div className="gesendet-knoepfe">
          {offen ? (
            <a href={`${zurBesichtigung}/${nachtrag ? "nachtrag" : "senden"}`} className="knopf-haupt knopf-block">
              <Ikon name="senden" /> {nachtrag ? "Zum Nachtrag" : "Zum Abschließen"}
            </a>
          ) : (
            <a href="#/" className="knopf-haupt knopf-block">
              <Ikon name="haus" /> Zur Startseite
            </a>
          )}
          {aufnahme && (
            <a href={zurBesichtigung} className="knopf-rand knopf-hell knopf-block">
              Besichtigung ansehen
            </a>
          )}
        </div>
      </main>
    </div>
  );
}
